import { isOpenArtistSlug } from "./artistSlug";

export const PAGE_BACKGROUND_KINDS = ["home", "release", "lists", "bio", "videos"] as const;
export type PageBackgroundKind = (typeof PAGE_BACKGROUND_KINDS)[number];

export const PAGE_BACKGROUND_STYLE_IDS = ["classic", "light-rays", "prism", "shape-waves", "drift-wall", "electric", "embers", "bokeh", "gallery"] as const;
export type PageBackgroundStyleId = (typeof PAGE_BACKGROUND_STYLE_IDS)[number];

export type PageBackgroundChoice = PageBackgroundStyleId | "auto" | "rotate";
export type PageBackgroundRotateBy = "day" | "visit";
export type PageBackgroundScope = "site-look" | "legacy";

export interface PageBackgroundStyleDef {
  id: PageBackgroundStyleId;
  label: string;
  hint: string;
  images: "none" | "cover" | "many";
  additive: boolean;
  audio: boolean;
  fps: number;
  renderScale: number;
  lightFallback?: PageBackgroundStyleId;
}

export const PAGE_BACKGROUND_STYLES: readonly PageBackgroundStyleDef[] = [
  { id: "classic", label: "Classic", hint: "Today's look: the sky and dotted wave on the homepage, the blurred cover on release pages, the plain page colour elsewhere.", images: "none", additive: false, audio: false, fps: 0, renderScale: 1 },
  { id: "light-rays", label: "Light rays", hint: "Soft shafts of the accent colour falling from above, breathing with the bass.", images: "none", additive: true, audio: true, fps: 24, renderScale: 0.5, lightFallback: "shape-waves" },
  { id: "prism", label: "Prism", hint: "A glass pyramid far behind the page, splitting the light into colour.", images: "none", additive: true, audio: true, fps: 24, renderScale: 0.5, lightFallback: "shape-waves" },
  { id: "shape-waves", label: "Shape waves", hint: "A quiet field of triangles, dots and squares that a slow tide keeps sorting.", images: "none", additive: false, audio: false, fps: 24, renderScale: 1 },
  { id: "drift-wall", label: "Drift wall", hint: "A tilted wall of artwork, columns drifting against each other. Release covers on the homepage, the release's own artwork on its page.", images: "many", additive: false, audio: false, fps: 30, renderScale: 1 },
  { id: "electric", label: "Electric halo", hint: "A live, crackling outline around the release artwork.", images: "none", additive: true, audio: true, fps: 20, renderScale: 1, lightFallback: "shape-waves" },
  { id: "embers", label: "Embers", hint: "Warm glints lifting off a deeply blurred cover.", images: "cover", additive: true, audio: true, fps: 30, renderScale: 0.75, lightFallback: "bokeh" },
  { id: "bokeh", label: "Bokeh", hint: "Big out of focus discs drifting over a deeply blurred cover.", images: "cover", additive: false, audio: true, fps: 24, renderScale: 0.5 },
  { id: "gallery", label: "Gallery", hint: "A slow cross fade through the release's downloadable artwork, pushed in like a film still.", images: "many", additive: false, audio: false, fps: 30, renderScale: 1 },
];

export const PAGE_BACKGROUND_ALLOW_LIST: Record<PageBackgroundKind, readonly PageBackgroundStyleId[]> = {
  home: ["classic", "shape-waves", "drift-wall"],
  // No "gallery": the owner found the full width pushed-in artwork looked low res.
  release: ["classic", "light-rays", "prism", "shape-waves", "electric", "embers", "bokeh", "drift-wall"],
  lists: ["classic", "light-rays", "prism", "shape-waves"],
  videos: ["classic", "light-rays", "prism", "shape-waves"],
  bio: ["classic", "bokeh", "light-rays", "shape-waves"],
};

// Styles the owner took off every page. Their renderers stay, so bringing one
// back is an allow-list change.
export const RETIRED_BACKGROUND_STYLES: readonly PageBackgroundStyleId[] = ["gallery"];

export const PAGE_BACKGROUND_KIND_LABELS: Record<PageBackgroundKind, string> = {
  home: "Homepage",
  release: "Release pages",
  lists: "Songs and albums lists",
  bio: "Bio",
  videos: "Videos",
};

export const MANY_IMAGES_MIN = 3;
export const BACKGROUND_IMAGE_CAP = 12;
export const RELEASE_OVERRIDE_CAP = 200;
export const PREVIEW_STORAGE_KEY = "truevined_bg_preview";
export const VISIT_SEED_STORAGE_KEY = "truevined_bg_visit_seed";
export const MIN_TEXT_CONTRAST = 4.5;

const STYLE_BY_ID = new Map<string, PageBackgroundStyleDef>(PAGE_BACKGROUND_STYLES.map((style) => [style.id, style]));
const ALBUM_ID_PATTERN = /^[A-Za-z0-9]{10,40}$/;

export function styleDef(id: PageBackgroundStyleId): PageBackgroundStyleDef {
  return STYLE_BY_ID.get(id) ?? (STYLE_BY_ID.get("classic") as PageBackgroundStyleDef);
}

export function isPageBackgroundStyleId(value: unknown): value is PageBackgroundStyleId {
  return typeof value === "string" && STYLE_BY_ID.has(value);
}

export function isPageBackgroundChoice(value: unknown): value is PageBackgroundChoice {
  return value === "auto" || value === "rotate" || isPageBackgroundStyleId(value);
}

export function styleOfferedOn(style: PageBackgroundStyleId, kind: PageBackgroundKind): boolean {
  return PAGE_BACKGROUND_ALLOW_LIST[kind].includes(style);
}

export function stylesForKind(kind: PageBackgroundKind): PageBackgroundStyleDef[] {
  return PAGE_BACKGROUND_ALLOW_LIST[kind].map((id) => styleDef(id));
}

export function backgroundScopeFor(artistSlug: string | null | undefined): PageBackgroundScope {
  return isOpenArtistSlug(artistSlug) ? "site-look" : "legacy";
}

export function artworkSourceFor(kind: PageBackgroundKind): "release" | "covers" {
  return kind === "release" ? "release" : "covers";
}

export interface PageBackgroundPageSettings {
  style: PageBackgroundChoice;
}

export interface PageBackgroundSettings {
  pages: Record<PageBackgroundKind, PageBackgroundPageSettings>;
  rotateBy: PageBackgroundRotateBy;
  releaseOverrides: Record<string, PageBackgroundStyleId>;
}

export function defaultPageBackgroundSettings(): PageBackgroundSettings {
  const pages = {} as Record<PageBackgroundKind, PageBackgroundPageSettings>;
  for (const kind of PAGE_BACKGROUND_KINDS) pages[kind] = { style: "classic" };
  return { pages, rotateBy: "day", releaseOverrides: {} };
}

function normalizePage(kind: PageBackgroundKind, raw: unknown): PageBackgroundPageSettings {
  const value = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  let style: PageBackgroundChoice = isPageBackgroundChoice(value.style) ? value.style : "classic";
  if (isPageBackgroundStyleId(style) && !styleOfferedOn(style, kind)) style = "classic";
  return { style };
}

export function normalizePageBackgroundSettings(value: unknown): PageBackgroundSettings {
  const raw = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const rawPages = raw.pages && typeof raw.pages === "object" ? (raw.pages as Record<string, unknown>) : {};
  const pages = {} as Record<PageBackgroundKind, PageBackgroundPageSettings>;
  for (const kind of PAGE_BACKGROUND_KINDS) pages[kind] = normalizePage(kind, rawPages[kind]);
  const rotateBy: PageBackgroundRotateBy = raw.rotateBy === "visit" ? "visit" : "day";
  const releaseOverrides: Record<string, PageBackgroundStyleId> = {};
  const rawOverrides = raw.releaseOverrides && typeof raw.releaseOverrides === "object" ? (raw.releaseOverrides as Record<string, unknown>) : {};
  for (const [albumId, style] of Object.entries(rawOverrides)) {
    if (Object.keys(releaseOverrides).length >= RELEASE_OVERRIDE_CAP) break;
    if (!ALBUM_ID_PATTERN.test(albumId)) continue;
    if (!isPageBackgroundStyleId(style) || !styleOfferedOn(style, "release")) continue;
    releaseOverrides[albumId] = style;
  }
  return { pages, rotateBy, releaseOverrides };
}

export function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b) >>> 0;
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35) >>> 0;
  hash ^= hash >>> 16;
  return hash >>> 0;
}

export function utcDayNumber(epochMs: number): number {
  return Math.floor(epochMs / 86_400_000);
}

export function rotationPool(kind: PageBackgroundKind): PageBackgroundStyleId[] {
  return PAGE_BACKGROUND_ALLOW_LIST[kind].filter((id) => id !== "classic");
}

export function rotatedStyle(kind: PageBackgroundKind, seed: number): PageBackgroundStyleId {
  const pool = rotationPool(kind);
  if (pool.length === 0) return "classic";
  return pool[hashString(`${kind}:${seed}`) % pool.length];
}

export interface ArtworkTone {
  hue: number;
  saturation: number;
  lightness: number;
}

export const NEUTRAL_TONE: ArtworkTone = { hue: 36, saturation: 0.2, lightness: 0.3 };

export function toneFromPixels(data: ArrayLike<number>): ArtworkTone {
  let hueX = 0;
  let hueY = 0;
  let saturationSum = 0;
  let lightnessSum = 0;
  let count = 0;
  for (let index = 0; index + 3 < data.length; index += 4) {
    if ((data[index + 3] ?? 255) < 16) continue;
    const red = (data[index] ?? 0) / 255;
    const green = (data[index + 1] ?? 0) / 255;
    const blue = (data[index + 2] ?? 0) / 255;
    const max = Math.max(red, green, blue);
    const min = Math.min(red, green, blue);
    const lightness = (max + min) / 2;
    const delta = max - min;
    const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1) || 1);
    let hue = 0;
    if (delta > 0) {
      if (max === red) hue = 60 * (((green - blue) / delta + 6) % 6);
      else if (max === green) hue = 60 * ((blue - red) / delta + 2);
      else hue = 60 * ((red - green) / delta + 4);
    }
    const weight = saturation * (1 - Math.abs(2 * lightness - 1));
    hueX += Math.cos((hue * Math.PI) / 180) * weight;
    hueY += Math.sin((hue * Math.PI) / 180) * weight;
    saturationSum += saturation;
    lightnessSum += lightness;
    count += 1;
  }
  if (count === 0) return NEUTRAL_TONE;
  const hue = hueX === 0 && hueY === 0 ? NEUTRAL_TONE.hue : ((Math.atan2(hueY, hueX) * 180) / Math.PI + 360) % 360;
  return { hue: Math.round(hue), saturation: Math.round((saturationSum / count) * 100) / 100, lightness: Math.round((lightnessSum / count) * 100) / 100 };
}

export interface ReleaseAutoContext {
  releaseKey: string | null | undefined;
  imageCount: number;
  tone: ArtworkTone;
  paletteLight: boolean;
}

export function isWarmHue(hue: number): boolean {
  const h = ((hue % 360) + 360) % 360;
  return h < 70 || h >= 330;
}

export function isCoolHue(hue: number): boolean {
  const h = ((hue % 360) + 360) % 360;
  return h >= 160 && h < 290;
}

export function autoReleaseWeights(context: Omit<ReleaseAutoContext, "releaseKey">): Array<[PageBackgroundStyleId, number]> {
  const { tone, imageCount, paletteLight } = context;
  const vivid = tone.saturation >= 0.35;
  const muted = tone.saturation < 0.18;
  const bright = tone.lightness >= 0.55;
  const warm = isWarmHue(tone.hue) && !muted;
  const cool = isCoolHue(tone.hue) && !muted;
  const weights = new Map<PageBackgroundStyleId, number>();
  const add = (style: PageBackgroundStyleId, weight: number) => weights.set(style, (weights.get(style) ?? 0) + weight);
  if (imageCount >= MANY_IMAGES_MIN) {
    add("gallery", 3);
    add("drift-wall", 3);
  }
  if (imageCount >= 1) {
    add(warm ? "embers" : "bokeh", 2);
    if (muted || bright) add("bokeh", 1.5);
  }
  add("light-rays", warm || bright ? 2.5 : 1.5);
  add("shape-waves", cool || muted ? 2 : 1);
  if (cool || vivid) add("prism", cool ? 2.5 : 1.5);
  if (vivid) add("electric", 1);
  return [...weights.entries()]
    .filter(([style]) => styleOfferedOn(style, "release") && !(paletteLight && styleDef(style).additive))
    .filter(([style]) => resolveRenderableStyle(style, { kind: "release", imageCount, paletteLight }) === style)
    .sort((a, b) => a[0].localeCompare(b[0]));
}

export function autoReleaseStyle(context: ReleaseAutoContext): PageBackgroundStyleId {
  const weights = autoReleaseWeights(context);
  const total = weights.reduce((sum, [, weight]) => sum + weight, 0);
  if (total <= 0) return "shape-waves";
  let point = ((hashString(`release:${context.releaseKey || "unknown"}`) % 100000) / 100000) * total;
  for (const [style, weight] of weights) {
    if (point < weight) return style;
    point -= weight;
  }
  return weights[weights.length - 1][0];
}

export const HOME_DRIFT_WALL_MIN = 6;

export function newestRelease<T extends { release_date?: string }>(releases: readonly T[] | null | undefined): T | undefined {
  let best: T | undefined;
  for (const release of releases ?? []) {
    if (!best || (release.release_date ?? "") > (best.release_date ?? "")) best = release;
  }
  return best;
}

export function autoHomeWeights(context: Omit<ReleaseAutoContext, "releaseKey">): Array<[PageBackgroundStyleId, number]> {
  const { tone, imageCount, paletteLight } = context;
  const vivid = tone.saturation >= 0.35;
  const muted = tone.saturation < 0.18;
  const warm = isWarmHue(tone.hue) && !muted;
  const cool = isCoolHue(tone.hue) && !muted;
  const weights = new Map<PageBackgroundStyleId, number>();
  const add = (style: PageBackgroundStyleId, weight: number) => weights.set(style, (weights.get(style) ?? 0) + weight);
  add("classic", warm ? 3 : vivid ? 2.5 : 2);
  add("shape-waves", cool || muted ? 2.5 : 1);
  if (paletteLight) add("shape-waves", 1);
  if (imageCount >= HOME_DRIFT_WALL_MIN) add("drift-wall", vivid ? 3 : 2.5);
  return [...weights.entries()]
    .filter(([style]) => styleOfferedOn(style, "home"))
    .filter(([style]) => resolveRenderableStyle(style, { kind: "home", imageCount, paletteLight }) === style)
    .sort((a, b) => a[0].localeCompare(b[0]));
}

export function autoHomeStyle(context: ReleaseAutoContext & { dayNumber: number }): PageBackgroundStyleId {
  const weights = autoHomeWeights(context);
  const total = weights.reduce((sum, [, weight]) => sum + weight, 0);
  if (total <= 0) return "classic";
  let point = ((hashString(`home:${context.dayNumber}:${context.releaseKey || "none"}`) % 100000) / 100000) * total;
  for (const [style, weight] of weights) {
    if (point < weight) return style;
    point -= weight;
  }
  return weights[weights.length - 1][0];
}

const AUTO_BY_KIND: Record<Exclude<PageBackgroundKind, "release">, PageBackgroundStyleId> = {
  home: "classic",
  lists: "light-rays",
  videos: "prism",
  bio: "bokeh",
};

export function usesArtworkContext(kind: PageBackgroundKind): boolean {
  return kind === "release" || kind === "home";
}

export function autoStyleFor(kind: PageBackgroundKind, context?: ReleaseAutoContext, nowMs?: number): PageBackgroundStyleId {
  if (kind === "release") return context ? autoReleaseStyle(context) : "classic";
  if (kind === "home" && context) return autoHomeStyle({ ...context, dayNumber: utcDayNumber(nowMs ?? 0) });
  return AUTO_BY_KIND[kind];
}

export interface ReleaseArtworkLayout {
  scale: number;
  compactScale: number;
  lifted: boolean;
}

const ARTWORK_LAYOUTS: Record<PageBackgroundStyleId, ReleaseArtworkLayout> = {
  classic: { scale: 1, compactScale: 1, lifted: false },
  "light-rays": { scale: 1.06, compactScale: 1, lifted: false },
  prism: { scale: 1.04, compactScale: 1, lifted: false },
  "shape-waves": { scale: 0.96, compactScale: 0.96, lifted: false },
  "drift-wall": { scale: 0.84, compactScale: 0.9, lifted: true },
  electric: { scale: 0.9, compactScale: 0.86, lifted: false },
  embers: { scale: 1, compactScale: 1, lifted: false },
  bokeh: { scale: 1, compactScale: 1, lifted: false },
  gallery: { scale: 0.86, compactScale: 0.9, lifted: true },
};

export function releaseArtworkLayout(style: PageBackgroundStyleId | null | undefined): ReleaseArtworkLayout {
  return (style && ARTWORK_LAYOUTS[style]) || ARTWORK_LAYOUTS.classic;
}

export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value.trim().replace(/^#/, "").toLowerCase();
  if (/^[0-9a-f]{6}$/.test(text)) return `#${text}`;
  if (/^[0-9a-f]{3}$/.test(text)) return `#${text.split("").map((char) => char + char).join("")}`;
  return null;
}

function channelsOf(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  return [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)];
}

function hexOf(channels: [number, number, number]): string {
  return `#${channels.map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0")).join("")}`;
}

export function mixColor(from: string, to: string, amount: number): string {
  const a = channelsOf(from);
  const b = channelsOf(to);
  return hexOf([a[0] + (b[0] - a[0]) * amount, a[1] + (b[1] - a[1]) * amount, a[2] + (b[2] - a[2]) * amount]);
}

export function relativeLuminance(hex: string): number {
  const linear = channelsOf(hex).map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

export function paletteIsLight(palette: { background: string }): boolean {
  const [red, green, blue] = channelsOf(normalizeHexColor(palette.background) ?? "#000000");
  return (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255 > 0.55;
}

export function contrastRatio(a: string, b: string): number {
  const first = relativeLuminance(a);
  const second = relativeLuminance(b);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

export const STYLE_ACCENT_COVERAGE: Record<PageBackgroundStyleId, number> = {
  classic: 0.16,
  "light-rays": 0.6,
  prism: 0.5,
  "shape-waves": 0.4,
  "drift-wall": 0.2,
  electric: 0.3,
  embers: 0.4,
  bokeh: 0.35,
  gallery: 0.16,
};

export function readableAccent(accent: string, background: string, text: string, style: PageBackgroundStyleId, minContrast = MIN_TEXT_CONTRAST): string {
  const coverage = STYLE_ACCENT_COVERAGE[style] ?? 0.5;
  const passes = (candidate: string) => contrastRatio(text, mixColor(background, candidate, coverage)) >= minContrast;
  if (passes(accent)) return accent;
  let low = 0;
  let high = 1;
  for (let step = 0; step < 12; step += 1) {
    const middle = (low + high) / 2;
    if (passes(mixColor(accent, background, middle))) high = middle;
    else low = middle;
  }
  return mixColor(accent, background, high);
}

export interface PageBackgroundPreview {
  style?: PageBackgroundChoice;
  color?: string;
  clear: boolean;
}

export function parseBackgroundPreview(search: string): PageBackgroundPreview {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const bg = params.get("bg")?.trim().toLowerCase() ?? null;
  const clear = bg === "reset" || bg === "off" || bg === "none";
  const style = !clear && isPageBackgroundChoice(bg) ? bg : undefined;
  const color = clear ? undefined : normalizeHexColor(params.get("bgColor")) ?? undefined;
  return { style, color, clear };
}

export function mergeBackgroundPreview(stored: PageBackgroundPreview | null, fromUrl: PageBackgroundPreview): PageBackgroundPreview | null {
  if (fromUrl.clear) return null;
  const style = fromUrl.style ?? stored?.style;
  const color = fromUrl.color ?? stored?.color;
  if (!style && !color) return null;
  return { style, color, clear: false };
}

export function sanitizeStoredPreview(value: unknown): PageBackgroundPreview | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const style = isPageBackgroundChoice(raw.style) ? raw.style : undefined;
  const color = normalizeHexColor(raw.color) ?? undefined;
  if (!style && !color) return null;
  return { style, color, clear: false };
}

export function withBackgroundParams(search: string, patch: { style?: PageBackgroundChoice | null; color?: string | null }): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  if (patch.style !== undefined) {
    if (patch.style === null) params.set("bg", "reset");
    else params.set("bg", patch.style);
  }
  if (patch.color !== undefined) {
    const color = normalizeHexColor(patch.color);
    if (color) params.set("bgColor", color.slice(1));
    else params.delete("bgColor");
  }
  if (params.get("bg") === "reset") params.delete("bgColor");
  const text = params.toString();
  return text ? `?${text}` : "";
}

export function backgroundSwitcherVisible(context: { isAdmin: boolean; previewActive: boolean; adMode: boolean; scope: PageBackgroundScope }): boolean {
  if (context.adMode || context.scope !== "site-look") return false;
  return context.isAdmin || context.previewActive;
}

export type PageBackgroundSource = "settings" | "preview" | "override" | "auto" | "rotate";

export interface PageBackgroundChoiceInput {
  kind: PageBackgroundKind;
  settings: PageBackgroundSettings;
  preview?: PageBackgroundPreview | null;
  releaseKey?: string | null;
  release?: Omit<ReleaseAutoContext, "releaseKey"> | null;
  nowMs: number;
  visitSeed?: number;
}

export interface PageBackgroundPlan {
  style: PageBackgroundStyleId;
  source: PageBackgroundSource;
  color?: string;
}

type ChoiceBase = Pick<PageBackgroundChoiceInput, "kind" | "settings" | "preview" | "releaseKey">;

export function requestedChoice(input: ChoiceBase): { choice: PageBackgroundChoice; source: PageBackgroundSource } {
  const override = input.kind === "release" && input.releaseKey ? input.settings.releaseOverrides[input.releaseKey] : undefined;
  if (input.preview?.style) return { choice: input.preview.style, source: "preview" };
  if (override) return { choice: override, source: "override" };
  return { choice: input.settings.pages[input.kind].style, source: "settings" };
}

export function needsReleaseContext(input: ChoiceBase): boolean {
  if (!usesArtworkContext(input.kind)) return false;
  const { choice } = requestedChoice(input);
  return choice === "auto" || (isPageBackgroundStyleId(choice) && styleDef(choice).images !== "none");
}

export function releaseContextRequired(input: ChoiceBase): boolean {
  return usesArtworkContext(input.kind) && requestedChoice(input).choice === "auto";
}

export function choosePageBackground(input: PageBackgroundChoiceInput): PageBackgroundPlan {
  const { kind, settings } = input;
  const requested = requestedChoice(input);
  let style: PageBackgroundStyleId;
  let source: PageBackgroundSource = requested.source;
  if (requested.choice === "auto") {
    style = autoStyleFor(kind, usesArtworkContext(kind) && input.release ? { ...input.release, releaseKey: input.releaseKey } : undefined, input.nowMs);
    if (source === "settings") source = "auto";
  } else if (requested.choice === "rotate") {
    const seed = settings.rotateBy === "visit" && typeof input.visitSeed === "number" ? input.visitSeed : utcDayNumber(input.nowMs);
    style = rotatedStyle(kind, seed);
    if (source === "settings") source = "rotate";
  } else {
    style = requested.choice;
  }
  if (!styleOfferedOn(style, kind)) style = "classic";
  if (usesArtworkContext(kind) && input.release) style = resolveRenderableStyle(style, { kind, ...input.release });
  return { style, source, ...(input.preview?.color ? { color: input.preview.color } : {}) };
}

export function planIsActive(plan: PageBackgroundPlan | null | undefined): boolean {
  return Boolean(plan && plan.style !== "classic");
}

export function classicLookVisible(plan: PageBackgroundPlan | null | undefined): boolean {
  if (plan === null) return true;
  if (plan === undefined) return false;
  return plan.style === "classic";
}

function firstOffered(kind: PageBackgroundKind, candidates: readonly PageBackgroundStyleId[]): PageBackgroundStyleId {
  return candidates.find((style) => styleOfferedOn(style, kind)) ?? "classic";
}

export function resolveRenderableStyle(style: PageBackgroundStyleId, context: { kind: PageBackgroundKind; imageCount: number; paletteLight: boolean }): PageBackgroundStyleId {
  let current = style;
  for (let step = 0; step < 4; step += 1) {
    const def = styleDef(current);
    if (def.images === "many" && context.imageCount < MANY_IMAGES_MIN) {
      current = firstOffered(context.kind, context.imageCount >= 1 ? ["bokeh", "shape-waves"] : ["shape-waves"]);
      continue;
    }
    if (def.images === "cover" && context.imageCount === 0) {
      current = firstOffered(context.kind, ["shape-waves"]);
      continue;
    }
    if (def.additive && context.paletteLight && def.lightFallback) {
      current = firstOffered(context.kind, [def.lightFallback, "shape-waves"]);
      continue;
    }
    break;
  }
  return current;
}

export function backgroundArtworkArgs(kind: PageBackgroundKind, release?: { spotifyAlbumId?: string; upc?: string; isrc?: string; spotifyTrackId?: string; name?: string } | null): Record<string, string> {
  if (kind !== "release" || !release) return {};
  const args: Record<string, string> = {};
  if (release.spotifyAlbumId) args.spotifyAlbumId = release.spotifyAlbumId;
  if (release.upc) args.upc = release.upc;
  if (release.isrc) args.isrc = release.isrc;
  if (release.spotifyTrackId) args.spotifyTrackId = release.spotifyTrackId;
  if (!args.spotifyAlbumId && !args.upc && !args.isrc && !args.spotifyTrackId && release.name) args.releaseName = release.name;
  return args;
}

export interface BackgroundImageCandidate {
  url: string;
  width?: number;
  height?: number;
  kind?: string;
  source: "asset" | "cover";
}

export interface SelectedBackgroundImage {
  url: string;
  width: number;
  height: number;
  source: "asset" | "cover";
  portrait: boolean;
}

const ASSET_KIND_RANK: Record<string, number> = {
  visual_still: 0,
  desktop_wallpaper: 1,
  phone_wallpaper: 2,
  story_card: 3,
  lyric_card: 5,
  cover_art: 6,
  other: 7,
};

export function isSafeBackgroundUrl(url: string): boolean {
  if (typeof url !== "string" || url.length > 2048) return false;
  if (!/^https:\/\//i.test(url) && !url.startsWith("/.netlify/images")) return false;
  if (/fl_attachment/i.test(url)) return false;
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)) return false;
  return true;
}

function dedupeKey(url: string): string {
  const cloudinary = url.match(/\/(?:upload|authenticated)\/(?:s--[^/]+--\/)?(?:[^/]*[,_][^/]*\/)*(?:v\d+\/)?(.+?)(?:\.[a-z0-9]+)?$/i);
  if (cloudinary) return `cld:${cloudinary[1].toLowerCase()}`;
  const spotify = url.match(/i\.scdn\.co\/image\/([a-z0-9]+)/i);
  if (spotify) return `scdn:${spotify[1].toLowerCase()}`;
  return url.toLowerCase();
}

export function selectBackgroundImages(
  candidates: readonly BackgroundImageCandidate[],
  options: { cap?: number; preferPortrait?: boolean; keepOrder?: boolean } = {},
): SelectedBackgroundImage[] {
  const cap = Math.max(0, Math.min(options.cap ?? BACKGROUND_IMAGE_CAP, BACKGROUND_IMAGE_CAP));
  const seen = new Set<string>();
  const kept: Array<SelectedBackgroundImage & { rank: number; order: number }> = [];
  candidates.forEach((candidate, order) => {
    if (!candidate || !isSafeBackgroundUrl(candidate.url)) return;
    const key = dedupeKey(candidate.url);
    if (seen.has(key)) return;
    seen.add(key);
    const width = candidate.width && candidate.width > 0 ? candidate.width : 640;
    const height = candidate.height && candidate.height > 0 ? candidate.height : 640;
    const portrait = height > width * 1.15;
    const sourceRank = candidate.source === "asset" ? 0 : 10;
    const kindRank = ASSET_KIND_RANK[candidate.kind ?? ""] ?? 4;
    const shapeRank = options.preferPortrait === undefined ? 0 : portrait === options.preferPortrait ? 0 : 3;
    const small = Math.max(width, height) < 480 ? 2 : 0;
    kept.push({ url: candidate.url, width, height, source: candidate.source, portrait, rank: options.keepOrder ? 0 : sourceRank + kindRank + shapeRank + small, order });
  });
  kept.sort((a, b) => a.rank - b.rank || a.order - b.order);
  return kept.slice(0, cap).map(({ url, width, height, source, portrait }) => ({ url, width, height, source, portrait }));
}

export const STATIC_FRAME_SEC = 6;

export function backgroundFrameTime(reducedMotion: boolean, elapsedSec: number): number {
  if (reducedMotion) return STATIC_FRAME_SEC;
  return Math.max(0, elapsedSec);
}

export function renderBudget(options: { viewportWidth: number; devicePixelRatio: number; lowEnd: boolean; renderScale: number; fps: number }): { dpr: number; fps: number; particleDensity: number } {
  const small = options.viewportWidth < 768;
  const maxDpr = small || options.lowEnd ? 1 : 1.5;
  const dpr = Math.max(0.35, Math.min(options.devicePixelRatio || 1, maxDpr) * options.renderScale);
  const fpsCap = small || options.lowEnd ? 24 : 30;
  return { dpr, fps: Math.max(1, Math.min(options.fps, fpsCap)), particleDensity: small || options.lowEnd ? 0.22 : 0.34 };
}
